import { useState, Fragment } from 'react';
import { useAuditLog, type AuditEntry } from '../api/queries/audit';
import { ApiError } from '../api/client';
import { fmtDateTime } from '../utils/date';
import { SortableTh } from '../components/SortableTh';
import { useClientSort } from '../hooks/useClientSort';
import './AuditLogPage.css';
import { TableScroll } from '../components/TableScroll';

type AuditSortKey = 'when' | 'who' | 'action' | 'entity' | 'summary';

function summarizeDiff(entry: AuditEntry): string {
  if (!entry.old_values && entry.new_values) {
    return `created ${entry.entity_type ?? ''}#${entry.entity_id ?? ''}`;
  }
  if (entry.old_values && !entry.new_values) {
    return `deleted ${entry.entity_type ?? ''}#${entry.entity_id ?? ''}`;
  }
  if (entry.old_values && entry.new_values) {
    const changed: string[] = [];
    for (const key of Object.keys(entry.new_values)) {
      const before = JSON.stringify(entry.old_values[key]);
      const after = JSON.stringify(entry.new_values[key]);
      if (before !== after) changed.push(key);
    }
    return changed.length === 0
      ? '(no diff)'
      : changed.slice(0, 5).join(', ') + (changed.length > 5 ? ` +${changed.length - 5} more` : '');
  }
  return entry.action;
}

export function AuditLogPage(): JSX.Element {
  const [filters, setFilters] = useState<{ entity_type?: string; user_id?: number; action?: string }>({});
  const [expandedId, setExpandedId] = useState<number | null>(null);

  const { data, isPending, error } = useAuditLog(filters);

  const entries = data?.audit ?? [];
  const sort = useClientSort(entries, (e, key: AuditSortKey) => {
    switch (key) {
      case 'when': return e.event_at;
      case 'who': return e.user_display_name ?? (e.user_id !== null ? `User #${e.user_id}` : 'system');
      case 'action': return e.action;
      case 'entity': return e.entity_type ? `${e.entity_type}#${e.entity_id ?? '?'}` : '';
      case 'summary': return summarizeDiff(e);
      default: return '';
    }
  }, { key: 'when', dir: 'desc' });

  if (isPending) return <p className="al-status">Loading…</p>;
  if (error) {
    const msg = error instanceof ApiError && error.status === 403
      ? "You don't have permission to view the audit log."
      : 'Failed to load.';
    return <p className="al-status al-error">{msg}</p>;
  }

  return (
    <section className="section-shell">
      <header className="section-header">
        <h2>Audit Log</h2>
        <div className="al-header-controls">
          <input
            type="text"
            className="form-input al-filter"
            placeholder="Filter by entity_type (e.g. invoices)"
            value={filters.entity_type ?? ''}
            onChange={(e) => setFilters({ ...filters, entity_type: e.target.value || undefined })}
          />
          <input
            type="text"
            className="form-input al-filter"
            placeholder="Filter by action (e.g. UPDATE)"
            value={filters.action ?? ''}
            onChange={(e) => setFilters({ ...filters, action: e.target.value || undefined })}
          />
          <input
            type="number"
            className="form-input al-filter al-filter-narrow"
            placeholder="user_id"
            value={filters.user_id ?? ''}
            onChange={(e) => {
              const n = Number(e.target.value);
              setFilters({ ...filters, user_id: Number.isInteger(n) && n > 0 ? n : undefined });
            }}
          />
        </div>
      </header>

      {entries.length === 0 ? (
        <div className="section-body">
          <p className="al-empty">No audit entries match.</p>
        </div>
      ) : (
        <div className="section-body section-body-flush">
          <TableScroll id="audit-log">
            <table className="al-table">
              <thead>
                <tr>
                  <SortableTh column="when" label="When" sortKey={sort.sortKey} sortDir={sort.sortDir} onSort={sort.toggle} />
                  <SortableTh column="who" label="Who" sortKey={sort.sortKey} sortDir={sort.sortDir} onSort={sort.toggle} />
                  <SortableTh column="action" label="Action" sortKey={sort.sortKey} sortDir={sort.sortDir} onSort={sort.toggle} />
                  <SortableTh column="entity" label="Entity" sortKey={sort.sortKey} sortDir={sort.sortDir} onSort={sort.toggle} />
                  <SortableTh column="summary" label="Summary" sortKey={sort.sortKey} sortDir={sort.sortDir} onSort={sort.toggle} />
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {sort.sorted.map((e) => {
                  const expanded = expandedId === e.id;
                  return (
                    <Fragment key={e.id}>
                      <tr className="al-row">
                        <td className="al-when">{fmtDateTime(e.event_at)}</td>
                        <td>{e.user_display_name ?? (e.user_id !== null ? `User #${e.user_id}` : 'system')}</td>
                        <td><code className="al-action">{e.action}</code></td>
                        <td>
                          {e.entity_type && (
                            <code className="al-entity">{e.entity_type}#{e.entity_id ?? '?'}</code>
                          )}
                        </td>
                        <td className="al-summary">{summarizeDiff(e)}</td>
                        <td>
                          <button
                            type="button"
                            className="al-link"
                            onClick={() => setExpandedId(expanded ? null : e.id)}
                          >
                            {expanded ? 'hide' : 'details'}
                          </button>
                        </td>
                      </tr>
                      {expanded && (
                        <tr className="al-detail-row">
                          <td colSpan={6}>
                            <div className="al-detail">
                              {e.ip_address && (
                                <div><strong>IP:</strong> <code>{e.ip_address}</code></div>
                              )}
                              {e.user_agent && (
                                <div><strong>UA:</strong> {e.user_agent}</div>
                              )}
                              {e.request_id && (
                                <div><strong>Request ID:</strong> <code>{e.request_id}</code></div>
                              )}
                              <div className="al-diff">
                                <div className="al-diff-side">
                                  <h4>Before</h4>
                                  <pre>{JSON.stringify(e.old_values ?? null, null, 2)}</pre>
                                </div>
                                <div className="al-diff-side">
                                  <h4>After</h4>
                                  <pre>{JSON.stringify(e.new_values ?? null, null, 2)}</pre>
                                </div>
                              </div>
                            </div>
                          </td>
                        </tr>
                      )}
                    </Fragment>
                  );
                })}
              </tbody>
            </table>
              </TableScroll>
        </div>
      )}
    </section>
  );
}
